import apiClient, { getAuthToken } from "../api/httpClient";
import endpoints from "../api/endpoints";
import { AI_VOICE_PROCESS_URL, BASE_URL } from "../config/env";

const trimTrailingSlash = (value) => String(value || "").replace(/\/+$/, "");
const voiceProcessUrl = String(AI_VOICE_PROCESS_URL || "").trim();
const AI_RAG_CHAT_URL =
  String(process.env.REACT_APP_AI_RAG_CHAT_URL || "").trim() ||
  (/\/pyv1\/voice\/process\/?$/.test(voiceProcessUrl)
    ? voiceProcessUrl.replace(/\/pyv1\/voice\/process\/?$/, "/pyv1/rag/chat")
    : "");
const configuredRagTimeoutMs = Number(process.env.REACT_APP_AI_RAG_TIMEOUT_MS);
const AI_RAG_TIMEOUT_MS =
  Number.isFinite(configuredRagTimeoutMs) && configuredRagTimeoutMs > 0
    ? configuredRagTimeoutMs
    : 60000;

const getAudioBaseUrl = () => trimTrailingSlash(BASE_URL);

const getAuthenticatedHeaders = (headers = {}) => {
  const token = getAuthToken();

  if (!token) {
    throw new Error("The authenticated user token is unavailable. Please log in again.");
  }

  return {
    ...headers,
    Authorization: `Bearer ${token}`,
  };
};

export const VOICE_VIDEO_END_BEHAVIOR_OPTIONS = [
  { value: "close", label: "Close popup when video ends" },
  { value: "stay", label: "Keep popup open when video ends" },
  { value: "loop", label: "Replay video when it ends" },
];

export const normalizeVoiceVideoEndBehavior = (value) =>
  VOICE_VIDEO_END_BEHAVIOR_OPTIONS.some((option) => option.value === value) ? value : "close";

const readFirstString = (objects, keys) => {
  for (const object of objects) {
    if (!object || typeof object !== "object") continue;

    for (const key of keys) {
      const value = key.split(".").reduce((acc, part) => acc?.[part], object);
      if (typeof value === "string" && value.trim()) {
        return value.trim();
      }
    }
  }

  return "";
};

const readFirstBoolean = (objects, keys) => {
  for (const object of objects) {
    if (!object || typeof object !== "object") continue;

    for (const key of keys) {
      const value = key.split(".").reduce((acc, part) => acc?.[part], object);
      if (typeof value === "boolean") return value;
      if (typeof value === "string" && value.trim()) {
        const normalized = value.trim().toLowerCase();
        if (["true", "1", "yes"].includes(normalized)) return true;
        if (["false", "0", "no"].includes(normalized)) return false;
      }
    }
  }

  return false;
};

const normalizeAudioSource = (value, mimeType) => {
  if (typeof value !== "string" || !value.trim()) return "";

  const cleanValue = value.trim();

  if (/^(https?:|data:|blob:)/i.test(cleanValue)) return cleanValue;

  if (cleanValue.startsWith("/")) {
    return `${getAudioBaseUrl()}${cleanValue}`;
  }

  const compact = cleanValue.replace(/\s/g, "");
  const looksLikeBase64 = compact.length > 96 && /^[A-Za-z0-9+/=]+$/.test(compact);

  if (looksLikeBase64) {
    return `data:${mimeType || "audio/mpeg"};base64,${compact}`;
  }

  return "";
};

const getAudioExtension = (mimeType) => {
  const type = String(mimeType || "").toLowerCase();
  if (type.includes("mp4")) return "m4a";
  if (type.includes("mpeg") || type.includes("mp3")) return "mp3";
  if (type.includes("wav")) return "wav";
  if (type.includes("ogg")) return "ogg";
  return "webm";
};

export const normalizeVoiceAssistantResponse = (payload) => {
  if (typeof payload === "string") {
    return {
      transcript: "",
      responseText: payload.trim(),
      sessionId: "",
      needsClarification: false,
      audioSource: "",
      raw: payload,
    };
  }

  const root = payload && typeof payload === "object" ? payload : {};
  const data = root.data && typeof root.data === "object" ? root.data : root;
  const nested = data.data && typeof data.data === "object" ? data.data : data;
  const result = data.result && typeof data.result === "object" ? data.result : {};
  const objects = [nested, result, data, root];

  const transcript = readFirstString(objects, [
    "transcript",
    "user_transcript",
    "user_text",
    "input_text",
    "recognized_text",
    "speech_text",
    "request.transcript",
    "request.text",
  ]);

  const sessionId = readFirstString(objects, ["session_id", "sessionId", "conversation_id"]);
  const audioValue = readFirstString(objects, [
    "audio_url",
    "audioUrl",
    "response_audio_url",
    "audio",
    "audio_base64",
    "response_audio",
  ]);
  const audioMimeType = readFirstString(objects, ["audio_mime_type", "audioMimeType", "mime_type"]);

  const rawResponseText = readFirstString(objects, [
    "assistant_response",
    "assistant_text",
    "response_text",
    "reply",
    "answer",
    "response",
    "output_text",
    "result_text",
    "message",
  ]);
  const normalizedResponseText = rawResponseText.toLowerCase();
  const responseText =
    ["ok", "success"].includes(normalizedResponseText) && (transcript || audioValue)
      ? ""
      : rawResponseText;

  const needsClarification =
    readFirstBoolean(objects, [
      "needs_clarification",
      "requires_clarification",
      "clarification_required",
      "needs_clear",
      "requires_clear",
      "clear_required",
      "need_clear",
    ]) ||
    ["clarification_required", "needs_clarification", "requires_clear"].includes(
      String(readFirstString(objects, ["status", "intent", "type", "stage"])).toLowerCase()
    );
  const candidatesSource =
    objects.find((object) => Array.isArray(object?.candidates))?.candidates || [];
  const candidates = candidatesSource.filter(
    (candidate) => typeof candidate === "string" && candidate.trim()
  );
  const clarificationReason = readFirstString(objects, ["reason", "clarification_reason"]);
  const requestId = readFirstString(objects, ["request_id", "requestId"]);

  return {
    transcript,
    responseText,
    sessionId,
    needsClarification,
    clarification: needsClarification
      ? {
          candidates,
          reason: clarificationReason,
          requestId,
        }
      : null,
    audioSource: normalizeAudioSource(audioValue, audioMimeType),
    raw: payload,
  };
};

export async function processVoiceCommand({
  audioBlob,
  sessionId,
  language,
  organizationId,
  signal,
}) {
  if (!audioBlob) {
    throw new Error("No audio recording was found.");
  }

  if (!organizationId) {
    throw new Error("Choose an organization before using the AI voice assistant.");
  }

  const formData = new FormData();
  const fileName = `voice-message.${getAudioExtension(audioBlob.type)}`;

  formData.append("audio", audioBlob, fileName);
  formData.append("session_id", sessionId || "");
  formData.append("language", language || "en");
  formData.append("org_id", String(organizationId));

  const headers = getAuthenticatedHeaders({
    Accept: "application/json",
  });

  const response = await apiClient.post(endpoints.aiVoice.process(), formData, {
    signal,
    withCredentials: true,
    headers,
  });

  return normalizeVoiceAssistantResponse(response.data);
}

export const normalizeAssistantChatResponse = (payload) => {
  if (typeof payload === "string") {
    return {
      text: payload.trim(),
      sources: [],
      success: true,
      raw: payload,
    };
  }

  const root = payload && typeof payload === "object" ? payload : {};
  const data = root.data && typeof root.data === "object" ? root.data : root;
  const text = readFirstString([data, root], [
    "data",
    "answer",
    "reply",
    "response",
    "message",
    "text",
  ]);

  return {
    text,
    sources: Array.isArray(data.sources)
      ? data.sources
      : Array.isArray(root.sources)
        ? root.sources
        : [],
    success:
      typeof data.success === "boolean"
        ? data.success
        : typeof root.success === "boolean"
          ? root.success
          : true,
    raw: payload,
  };
};

const toPositiveInteger = (value) => {
  const normalized = Number(value);
  return Number.isInteger(normalized) && normalized > 0 ? normalized : null;
};

export async function sendAssistantChatMessage({
  prompt,
  subscriberId,
  organizationId,
  signal,
}) {
  const cleanPrompt = String(prompt || "").trim();
  const subid = toPositiveInteger(subscriberId);
  const organization_id = toPositiveInteger(organizationId);

  if (!cleanPrompt) {
    throw new Error("Write a message before sending.");
  }

  if (!AI_RAG_CHAT_URL) {
    throw new Error("The AI chat URL is not configured.");
  }

  if (!subid) {
    throw new Error("Authenticated subscriber is required.");
  }

  if (!organization_id) {
    throw new Error("Choose an organization before using chat.");
  }

  const headers = getAuthenticatedHeaders({
    Accept: "application/json",
    "Content-Type": "application/json",
  });

  const response = await apiClient.post(
    AI_RAG_CHAT_URL,
    {
      prompt: cleanPrompt,
      subid,
      organization_id,
    },
    {
      signal,
      headers,
      timeout: AI_RAG_TIMEOUT_MS,
      withCredentials: true,
    }
  );

  return normalizeAssistantChatResponse(response.data);
}

export const BROWSER_VOICE_COMMAND_CONFIG_UPDATED_EVENT = "sama-browser-voice-command-config-updated";

export const normalizeVoiceCommandConfig = (value = {}) => {
  const source = value && typeof value === "object" ? value : {};
  const normalizeBasicAction = (action, index) => {
    const sourceAction = action && typeof action === "object" ? action : {};
    const rawAction = sourceAction.action && typeof sourceAction.action === "object"
      ? sourceAction.action
      : { type: "media", media: {} };
    const media = rawAction.media && typeof rawAction.media === "object" ? rawAction.media : {};

    return {
      ...sourceAction,
      id: String(sourceAction.id || `basic-${index + 1}`),
      label: String(sourceAction.label || `Command ${index + 1}`),
      phrases: Array.isArray(sourceAction.phrases) && sourceAction.phrases.length
        ? sourceAction.phrases
        : ["hello sama", "hey sama", "مرحبا سما"],
      action: rawAction.type === "navigate"
        ? {
            ...rawAction,
            type: "navigate",
            path: String(rawAction.path || "/"),
          }
        : {
            ...rawAction,
            type: "media",
            media: {
              ...media,
              path: String(media.path || rawAction.path || ""),
              title: String(media.title || ""),
              mediaType: ["image", "video", "file"].includes(media.mediaType) ? media.mediaType : "image",
              videoEndBehavior: normalizeVoiceVideoEndBehavior(media.videoEndBehavior),
            },
          },
    };
  };
  const subscriberBasicActions =
    source.subscriberBasicActions && typeof source.subscriberBasicActions === "object" && !Array.isArray(source.subscriberBasicActions)
      ? Object.entries(source.subscriberBasicActions).reduce((acc, [subscriberId, actions]) => {
          if (Array.isArray(actions)) acc[String(subscriberId)] = actions.map(normalizeBasicAction);
          return acc;
        }, {})
      : {};

  return {
    enabled: source.enabled !== false,
    mode: source.mode === "advanced" ? "advanced" : "basic",
    wakeWords: Array.isArray(source.wakeWords) && source.wakeWords.length ? source.wakeWords : ["sama"],
    languages: Array.isArray(source.languages) && source.languages.length ? source.languages : ["en-US"],
    deniedMessage:
      source.deniedMessage ||
      "Sorry, we can't proceed your request since you don't have the necessary permission.",
    unknownMessage:
      source.unknownMessage ||
      "Sorry, we can't proceed your request now, please contact us to describe your issue.",
    basicActions: Array.isArray(source.basicActions) ? source.basicActions.map(normalizeBasicAction) : [],
    subscriberBasicActions,
    advanced: {
      pages: Array.isArray(source.advanced?.pages) ? source.advanced.pages : [],
      entities: Array.isArray(source.advanced?.entities) ? source.advanced.entities : [],
    },
  };
};

export const notifyBrowserVoiceCommandConfigUpdated = (value = {}) => {
  const nextConfig = normalizeVoiceCommandConfig(value);

  if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
    window.dispatchEvent(new CustomEvent(BROWSER_VOICE_COMMAND_CONFIG_UPDATED_EVENT, {
      detail: nextConfig,
    }));
  }

  return nextConfig;
};


export async function fetchVoiceCommandConfig(organizationId) {
  const response = await apiClient.get(endpoints.configuration.public(), {
    params: organizationId ? { organization_id: organizationId } : undefined,
    withCredentials: true,
  });
  return normalizeVoiceCommandConfig(response.data?.data?.aiVoiceCommands);
}

export async function resolveTextVoiceCommand({ transcript, language, organizationId, signal }) {
  const response = await apiClient.post(
    endpoints.aiVoice.resolveText(),
    {
      transcript,
      language,
      organization_id: organizationId,
    },
    {
      signal,
      withCredentials: true,
      headers: { Accept: "application/json" },
    }
  );

  return response.data?.data || response.data;
}

export async function fetchVoiceMediaLibrary() {
  const response = await apiClient.get(endpoints.aiVoice.mediaLibrary(), {
    withCredentials: true,
  });
  return Array.isArray(response.data?.data) ? response.data.data : [];
}

export async function uploadVoiceMedia({ file, title }) {
  const formData = new FormData();
  formData.append("file", file);
  if (title) formData.append("title", title);

  const response = await apiClient.post(endpoints.aiVoice.uploadMedia(), formData, {
    withCredentials: true,
    headers: { Accept: "application/json" },
  });

  return response.data?.data;
}
